import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DigitalDesign() {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editDesign, setEditDesign] = useState(null);
  const [formData, setFormData] = useState({
    design_number: '',
    article_id: '',
    article_name: '',
    designer: '',
    design_file_url: '',
    status: 'draft'
  });

  useEffect(() => {
    fetchDesigns();
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data?entity_type=article`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles');
    }
  };

  const fetchDesigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/digital-designs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDesigns(response.data);
    } catch (error) {
      toast.error('Failed to fetch designs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editDesign) {
        const payload = { ...formData, id: editDesign.id };
        await axios.put(`${API}/digital-designs/${editDesign.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/digital-designs`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditDesign(null);
      setFormData({ design_number: '', article_id: '', article_name: '', designer: '', design_file_url: '', status: 'draft' });
      fetchDesigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/digital-designs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      fetchDesigns();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const filteredDesigns = (status) => designs.filter(d => !status || d.status === status);

  const renderDesignsTable = (filteredList) => (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Design #</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designer</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredList.map((design) => (
            <tr key={design.id} data-testid={`design-row-${design.id}`}>
              <td className="px-6 py-4">{design.design_number}</td>
              <td className="px-6 py-4">{design.article_name || 'N/A'}</td>
              <td className="px-6 py-4">{design.designer}</td>
              <td className="px-6 py-4">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  design.status === 'approved' ? 'bg-green-100 text-green-800' :
                  design.status === 'in_review' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>{design.status}</span>
              </td>
              <td className="px-6 py-4">
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => { setEditDesign(design); setFormData(design); setOpen(true); }} data-testid={`edit-design-${design.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(design.id)} data-testid={`delete-design-${design.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredList.length === 0 && <div className="text-center py-12" data-testid="no-designs"><p className="text-gray-500">No designs found</p></div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Digital Design</h1>
              <p className="text-sm text-gray-600">Design management</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex justify-end mb-6">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditDesign(null); setFormData({ design_number: '', article_id: '', article_name: '', designer: '', design_file_url: '', status: 'draft' }); }} data-testid="add-design-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Design
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="design-dialog">
              <DialogHeader>
                <DialogTitle>{editDesign ? 'Edit Design' : 'Create Design'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Design Number</Label>
                  <Input value={formData.design_number} onChange={(e) => setFormData({ ...formData, design_number: e.target.value })} required data-testid="design-number-input" />
                </div>
                <div className="space-y-2">
                  <Label>Article (Optional)</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.article_id}
                    onChange={(e) => {
                      const article = articles.find(a => a.id === e.target.value);
                      setFormData({ ...formData, article_id: e.target.value, article_name: article?.name || '' });
                    }} data-testid="article-select">
                    <option value="">Select Article</option>
                    {articles.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Designer</Label>
                  <Input value={formData.designer} onChange={(e) => setFormData({ ...formData, designer: e.target.value })} required data-testid="designer-input" />
                </div>
                <div className="space-y-2">
                  <Label>Design File URL</Label>
                  <Input value={formData.design_file_url} onChange={(e) => setFormData({ ...formData, design_file_url: e.target.value })} placeholder="https://..." data-testid="file-url-input" />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} data-testid="status-select">
                    <option value="draft">Draft</option>
                    <option value="in_review">In Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-design">{editDesign ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="all" data-testid="all-tab">All Designs</TabsTrigger>
            <TabsTrigger value="draft" data-testid="draft-tab">Drafts</TabsTrigger>
            <TabsTrigger value="in_review" data-testid="review-tab">In Review</TabsTrigger>
            <TabsTrigger value="approved" data-testid="approved-tab">Approved</TabsTrigger>
          </TabsList>
          <TabsContent value="all">{renderDesignsTable(designs)}</TabsContent>
          <TabsContent value="draft">{renderDesignsTable(filteredDesigns('draft'))}</TabsContent>
          <TabsContent value="in_review">{renderDesignsTable(filteredDesigns('in_review'))}</TabsContent>
          <TabsContent value="approved">{renderDesignsTable(filteredDesigns('approved'))}</TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
